"use client";
import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormFeedback,
  Input,
  Label,
  Button,
  Row,
  Col,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { changePassword } from "utils/apiService";

const ChangePasswordPage = () => {
  const [modalOpen, setModalOpen] = useState(true);
  const [userId, setUserId] = useState();

  // State to toggle password visibility
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const toggle = () => {
    setModalOpen(false);
    window.history.back();
    // go back to previous page
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const user = JSON.parse(storedUser);
      setUserId(user.id);
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      currentPassword: Yup.string().required("Current password is required"),
      newPassword: Yup.string()
        .required("New password is required")
        .matches(
          /^(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
          "Password must be at least 8 characters, include one number and one special character"
        ),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords must match")
        .required("Confirm your new password"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          id: userId,
          current_password: values.currentPassword,
          new_password: values.newPassword,
        };

        console.log("Password change payload:", payload);

        const response = await changePassword(payload);

        if (response.success) {
          toast.success("Password updated successfully!");

          setTimeout(() => {
            resetForm();
            toggle();
            window.location.href = "/login";
          }, 1000);
        } else {
          toast.error("Something went wrong!");
        }
      } catch (error) {
        toast.error("Failed to change password");
      }
    },
  });

  const toggleVisibility = (field: "current" | "new" | "confirm") => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <Modal isOpen={modalOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>Change Password</ModalHeader>
      <ModalBody>
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={12}>
              <div className="mb-3">
                <Label>Current Password</Label>
                <InputGroup>
                  <Input
                    type={showPassword.current ? "text" : "password"}
                    name="currentPassword"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.currentPassword}
                    invalid={
                      formik.touched.currentPassword &&
                      !!formik.errors.currentPassword
                    }
                  />
                  <InputGroupText
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleVisibility("current")}
                  >
                    <i
                      className={`bx ${
                        showPassword.current ? "bx-show" : "bx-hide"
                      }`}
                    />
                  </InputGroupText>
                </InputGroup>
                <FormFeedback>{formik.errors.currentPassword}</FormFeedback>
              </div>

              <div className="mb-3">
                <Label>New Password</Label>
                <InputGroup>
                  <Input
                    type={showPassword.new ? "text" : "password"}
                    name="newPassword"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.newPassword}
                    invalid={
                      formik.touched.newPassword && !!formik.errors.newPassword
                    }
                  />
                  <InputGroupText
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleVisibility("new")}
                  >
                    <i
                      className={`bx ${
                        showPassword.new ? "bx-show" : "bx-hide"
                      }`}
                    />
                  </InputGroupText>
                </InputGroup>
                <FormFeedback>{formik.errors.newPassword}</FormFeedback>
              </div>

              <div className="mb-3">
                <Label>Confirm New Password</Label>
                <InputGroup>
                  <Input
                    type={showPassword.confirm ? "text" : "password"}
                    name="confirmPassword"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.confirmPassword}
                    invalid={
                      formik.touched.confirmPassword &&
                      !!formik.errors.confirmPassword
                    }
                  />
                  <InputGroupText
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleVisibility("confirm")}
                  >
                    <i
                      className={`bx ${
                        showPassword.confirm ? "bx-show" : "bx-hide"
                      }`}
                    />
                  </InputGroupText>
                </InputGroup>
                <FormFeedback>{formik.errors.confirmPassword}</FormFeedback>
              </div>
            </Col>
          </Row>

          <div className="text-end">
            <Button color="primary" type="submit">
              Submit
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default ChangePasswordPage;
